import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import { bugReportsApi } from '@/api/services/bug-reports.api';
import { handleMutationError } from '@/lib/handle-mutation-error';
import { BUG_REPORTS_KEYS, MY_BUG_REPORTS_PAGE_SIZE } from '../constants';
import type { IAddBugReportComment, ICreateBugReport } from '../types';

export function useCreateBugReport() {
    const { t } = useTranslation();
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: (input: ICreateBugReport) => bugReportsApi.create(input),
        onSuccess: () => {
            void queryClient.invalidateQueries({ queryKey: BUG_REPORTS_KEYS.lists() });
            toast.success(
                t('bugReports.toast.created', 'Thanks — your report has been sent to our team'),
            );
        },
        onError: (error: unknown) => {
            handleMutationError(error, t('bugReports.toast.createError', 'Failed to send report'));
        },
    });
}

/**
 * The reporter's own reports, newest first. Only mounted with the "My reports"
 * tab, so nothing hits the wire for the many "?" clicks that end in someone
 * filing something new instead.
 */
export function useMyBugReports() {
    const params = { page: 1, limit: MY_BUG_REPORTS_PAGE_SIZE } as const;

    return useQuery({
        queryKey: BUG_REPORTS_KEYS.list(params),
        queryFn: () => bugReportsApi.list(params),
    });
}

/**
 * One report with its full thread. Only fetched once a row is expanded — the
 * list response carries no comments, and pre-fetching every thread would mean
 * one round trip per report for threads nobody opened.
 */
export function useBugReportDetail(id: string | null) {
    return useQuery({
        queryKey: BUG_REPORTS_KEYS.detail(id ?? ''),
        queryFn: () => bugReportsApi.getById(id ?? ''),
        enabled: id !== null,
    });
}

/**
 * Reply on an existing report. Replying to a closed report reopens it server
 * side, so the list is invalidated too — the status badge there is now stale.
 */
export function useAddBugReportComment(bugReportId: string) {
    const { t } = useTranslation();
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: (input: IAddBugReportComment) => bugReportsApi.addComment(bugReportId, input),
        onSuccess: () => {
            void queryClient.invalidateQueries({
                queryKey: BUG_REPORTS_KEYS.detail(bugReportId),
            });
            void queryClient.invalidateQueries({ queryKey: BUG_REPORTS_KEYS.lists() });
        },
        onError: (error: unknown) => {
            handleMutationError(error, t('bugReports.toast.replyError', 'Failed to send reply'));
        },
    });
}
